import { type CustomerOrderListItemDto } from './customer-order-list-item-dto';
import {
  Beverage,
  Customer,
  CustomerOrder,
  CustomerSetting,
  MealCategory,
  Route,
  ScheduledMeal,
  Site,
  SiteMenuBeverageOrder,
  SiteMenuOrder,
  SiteMenuSupplyOrder,
  Supply,
} from '../../entities';
import CustomerOrderRevision from '../../entities/customer-order-revision';
import { type OrderStatus, type ServiceType } from '../../types';
import { LocalDate } from '../../utils/local-date';

interface BeverageDetailDto {
  id: number;
  name: string;
  description: string | null;
  /** The beverage's own price, only sent to HHC_ADMIN and CUSTOMER_ADMIN */
  price: number | null;
  isExtra: boolean;
}

interface SupplyDetailDto {
  id: number;
  name: string;
  description: string | null;
  /** The supply's own price, only sent to HHC_ADMIN and CUSTOMER_ADMIN */
  price: number | null;
}

interface MealCategoryDetailDto {
  id: number;
  name: string;
  abbreviation: string;
  description: string;
  sortOrder: number;
  isCompleteMeal: boolean;
  units: number;
}

/**
 * The meal a line item was ordered for. Unlike the order options' meal it carries no beverages or
 * supplies to choose from: what the line was ordered with is on the line itself.
 */
interface ScheduledMealDetailDto {
  id: number;
  day: string;
  /** Only sent to HHC_ADMIN and CUSTOMER_ADMIN */
  price: number | null;
  name: string;
  mealItemsNames: string[];
  mealCategory: MealCategoryDetailDto | null;
}

interface SiteMenuBeverageOrderDetailDto {
  id: number;
  quantity: number;
  unitPrice: number | null;
  totalPrice: number | null;
  beverageName: string;
  /** Whether the beverage was ordered as an extra one is the beverage's own "isExtra" */
  beverage: BeverageDetailDto | null;
}

interface SiteMenuSupplyOrderDetailDto {
  id: number;
  quantity: number;
  unitPrice: number | null;
  totalPrice: number | null;
  supplyName: string;
  supply: SupplyDetailDto | null;
}

/**
 * A line item of an order, as returned by the customer order and archived customer order controllers
 */
export interface SiteMenuOrderDetailDto {
  id: number;
  quantity: number;
  mainType: 'Regular' | 'Diabetic';
  serviceType: ServiceType;
  comment: string | null;
  prepackBeverages: boolean | null;
  diets: string | null;
  preferences: string | null;
  unitPrice: number | null;
  totalPrice: number | null;
  site: {
    id: number;
    name: string;
    county: string;
    siteNumber: string | null;
    custSiteNumber: string;
    displaySiteNumber: string;
  } | null;
  route: { id: number; name: string } | null;
  scheduledMeal: ScheduledMealDetailDto | null;
  siteMenuBeverageOrders: SiteMenuBeverageOrderDetailDto[];
  siteMenuSupplyOrders: SiteMenuSupplyOrderDetailDto[];
}

/**
 * A customer order with the line items of its latest revision, as returned by the customer order controller
 */
export interface CustomerOrderDetailDto {
  id: number;
  orderDate: string;
  status: OrderStatus;
  latestRevisionId: number | null;
  customer: CustomerOrderListItemDto['customer'];
  siteMenuOrders: SiteMenuOrderDetailDto[];
}

const toBeverage = (item: BeverageDetailDto): Beverage =>
  Object.assign(new Beverage(), {
    id: String(item.id),
    name: item.name,
    description: item.description ?? '',
    price: item.price ?? 0,
    isExtra: item.isExtra,
  });

const toSupply = (item: SupplyDetailDto): Supply =>
  Object.assign(new Supply(), {
    id: String(item.id),
    name: item.name,
    description: item.description ?? '',
    price: item.price ?? 0,
  });

const toScheduledMeal = (item: ScheduledMealDetailDto): ScheduledMeal =>
  Object.assign(new ScheduledMeal(), {
    id: String(item.id),
    day: new LocalDate(item.day),
    price: item.price ?? 0,
    name: item.name,
    mealItemsNames: item.mealItemsNames,
    mealCategory: Object.assign(new MealCategory(), {
      ...(item.mealCategory ?? {}),
      id: item.mealCategory ? String(item.mealCategory.id) : undefined,
    }),
  });

const toSiteMenuBeverageOrder = (item: SiteMenuBeverageOrderDetailDto): SiteMenuBeverageOrder =>
  Object.assign(new SiteMenuBeverageOrder(), {
    id: String(item.id),
    quantity: item.quantity,
    isExtraBeverage: item.beverage?.isExtra ?? false,
    unitPrice: item.unitPrice ?? 0,
    totalPrice: item.totalPrice ?? 0,
    beverageName: item.beverageName,
    beverage: item.beverage ? toBeverage(item.beverage) : undefined,
  });

const toSiteMenuSupplyOrder = (item: SiteMenuSupplyOrderDetailDto): SiteMenuSupplyOrder =>
  Object.assign(new SiteMenuSupplyOrder(), {
    id: String(item.id),
    quantity: item.quantity,
    unitPrice: item.unitPrice ?? 0,
    totalPrice: item.totalPrice ?? 0,
    supplyName: item.supplyName,
    supply: item.supply ? toSupply(item.supply) : undefined,
  });

/**
 * Maps a line item to a SiteMenuOrder shaped like the ones loaded through the JSON:API, so that it can be passed to
 * the components which still use the JSON:API
 */
// TODO: Update this funtion to use one SiteMenuOrder structure after migrating from JSON:API
export const toSiteMenuOrder = (item: SiteMenuOrderDetailDto): SiteMenuOrder =>
  Object.assign(new SiteMenuOrder(), {
    id: String(item.id),
    quantity: item.quantity,
    mainType: item.mainType,
    serviceType: item.serviceType,
    comment: item.comment ?? undefined,
    prepackBeverages: item.prepackBeverages ?? false,
    diets: item.diets,
    preferences: item.preferences,
    unitPrice: item.unitPrice ?? 0,
    totalPrice: item.totalPrice ?? 0,
    mealName: item.scheduledMeal?.name ?? '',
    mealCategoryName: item.scheduledMeal?.mealCategory?.name ?? '',
    routeName: item.route?.name ?? '',
    site: item.site
      ? Object.assign(new Site(), {
          ...item.site,
          id: String(item.site.id),
        })
      : undefined,
    route: item.route
      ? Object.assign(new Route(), { id: String(item.route.id), name: item.route.name })
      : undefined,
    scheduledMeal: item.scheduledMeal ? toScheduledMeal(item.scheduledMeal) : undefined,
    siteMenuBeverageOrders: item.siteMenuBeverageOrders.map(toSiteMenuBeverageOrder),
    siteMenuSupplyOrders: item.siteMenuSupplyOrders.map(toSiteMenuSupplyOrder),
  });

/**
 * Maps an order to a CustomerOrder shaped like the ones loaded through the JSON:API. The line items hang off the
 * latest revision, which is where CustomerOrder#siteMenuOrders reads them from
 */
// TODO: Update this funtion to use one CustomerOrder structure after migrating from JSON:API
export const toCustomerOrderDetail = (item: CustomerOrderDetailDto): CustomerOrder =>
  Object.assign(new CustomerOrder(), {
    id: String(item.id),
    orderDate: new LocalDate(item.orderDate),
    status: item.status,
    latestRevisionId: item.latestRevisionId != null ? String(item.latestRevisionId) : undefined,
    customer: Object.assign(new Customer(), {
      id: item.customer ? String(item.customer.id) : undefined,
      name: item.customer?.name,
      customerSetting: Object.assign(new CustomerSetting(), item.customer?.customerSetting ?? {}),
    }),
    latestRevision: Object.assign(new CustomerOrderRevision(), {
      id: item.latestRevisionId != null ? String(item.latestRevisionId) : undefined,
      siteMenuOrders: item.siteMenuOrders.map(toSiteMenuOrder),
    }),
  });
